import { useCallback, useState } from 'react';

import { readProxyArrayValue } from '../../../modules/properties/services/adapter';
import {
  getColorFromName,
  getInitials,
} from '../../../modules/properties/services/attendee-id-utils';
import { useManager } from './common';
import * as memberStyles from './members-avatar.css';
import type { PropertyRowValueProps } from './property-row-value-renderer';
import * as styles from './styles.css';

// Component to display a single member avatar
export const MemberAvatar = ({
  name,
  index,
}: {
  name: string;
  index: number;
}) => {
  const initials = getInitials(name);
  const backgroundColor = getColorFromName(name);

  return (
    <div
      className={`${memberStyles.memberAvatar} ${index > 0 ? memberStyles.memberAvatarNotFirst : ''}`}
      style={{ backgroundColor }}
      title={name}
    >
      {initials}
    </div>
  );
};

// Component to display a list of member avatars
export const MembersAvatarList = ({ members }: { members: string[] }) => {
  if (!members || members.length === 0) return null;

  return (
    <div className={memberStyles.memberAvatarStack}>
      {members.map((member, index) => (
        <MemberAvatar key={`${member}-${index}`} name={member} index={index} />
      ))}
    </div>
  );
};

// Main component for the Members property value
export const MembersValue = ({ property }: PropertyRowValueProps) => {
  const manager = useManager();
  const [inputValue, setInputValue] = useState('');
  const members = readProxyArrayValue<string>(property.value);

  const handleAddMember = useCallback(() => {
    if (!inputValue.trim()) return;

    const newMembers = [...members, inputValue.trim()];
    manager.updateCustomProperty(property.id, {
      value: newMembers,
    });
    setInputValue('');
  }, [inputValue, members, manager, property.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddMember();
      }
    },
    [handleAddMember]
  );

  return (
    <div
      className={styles.propertyRowValueCell}
      data-empty={!members || members.length === 0}
    >
      {members && members.length > 0 ? (
        <div className={memberStyles.membersAvatarContainer}>
          <MembersAvatarList members={members} />
          <input
            className={memberStyles.memberAvatarInput}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleAddMember}
            placeholder={'Add member...'}
          />
        </div>
      ) : (
        <input
          className={memberStyles.memberAvatarInput}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleAddMember}
          placeholder={'Empty'}
        />
      )}
    </div>
  );
};
